import React, { useRef, useEffect, useMemo, useCallback } from 'react'
import isEqual from 'lodash/isEqual'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'

export const SmartSubmitContext = React.createContext()

export const useSmartSubmit = ({ formApi, formController, onSubmit }) => {
	const isSubmitting = useRef(false)
	const submit = useRef()

	const valuesChanged = useCallback((formState, field) => {
		const { initialValues } = formController.options
		const oldValue = field ? get(initialValues, field) : initialValues
		const newValue = field ? get(formState.values, field) : formState.values
		// Workaround for object item fields like 'object.key' with null values are not stored in formState.values
		if((oldValue === null || oldValue === '') && typeof newValue === 'undefined') {
			return false
		}
		return !isEqual(oldValue, newValue)
	}, [formController])

	const out = useMemo(() => {
		const handleSubmitField = field => {
			const formState = formApi.getState()
			if(valuesChanged(formState, field)) {
				formApi.submitForm()
			}
			isSubmitting.current = false
		}

		if(!submit.current) {
			submit.current = debounce(handleSubmitField, 2000, { leading: false })
		}

		const startSubmit = field => e => {
			isSubmitting.current = true

			submit.current(field)
		}

		const delaySubmit = (field) => () => {
			if(isSubmitting.current) {
				submit.current.cancel()
				submit.current(field)
			}
		}

		const immidiateSubmit = field => e => {
			if(!isSubmitting.current) {
				handleSubmitField(field)
			}
			submit.current.flush()
		}

		return {
			submit: startSubmit,
			immidiateSubmit: immidiateSubmit,
			delaySubmit: delaySubmit
		}
	}, [valuesChanged, formApi])

	const handleManualSubmit = useCallback(() => {
		formApi.validate()
		const formState = formApi.getState()

		if(onSubmit && !formState.invalid && valuesChanged(formState)) {
			if(onSubmit) {
				onSubmit(cloneDeep(formState.values))
			}
		}
	}, [formController, onSubmit, valuesChanged])

	useEffect(() => {
		return () => {
			if(isSubmitting.current) {
				submit.current.cancel()
				handleManualSubmit()
			}
		}
	// only execute this when component is unmounting
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return out
}

export const withFieldSubmit = WrapedComponent => ({ immidiateSubmit, ...props }) => (
	<SmartSubmitContext.Consumer>
		{context => {
			const newProps = {
				...props,
				smartSubmit: typeof context !== 'object' ? null : {
					submit: immidiateSubmit ?
						context.immidiateSubmit(props.field) :
						context.submit(props.field),
					delay: context.delaySubmit(props.field),
					// Sometimes submit with delay and immidiate submit is needed together
					immidiateSubmit: context.immidiateSubmit(props.field)
				}
			}
			return <WrapedComponent {...newProps}/>
		}
		}
	</SmartSubmitContext.Consumer>
)
